import React from "react";
import {
  Box,
  Button,
  Drawer,
  Header,
  Icon,
  IconProps,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import * as tokens from "@amzn/awsui-design-tokens-core";
import {
  InvestigationInboxItem,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  useInvestigationInbox,
  useMarkInboxRead,
} from "../../hooks/useInvestigations";
import { formatTimestamp, notificationLabel } from "./investigationUtils";

/** Entries shown in the pane; the inbox endpoint returns at most 50 per page anyway. */
const PANE_LIMIT = 30;

/** "16:59" today, "28 Sep 16:59" otherwise; the full timestamp is the hover title. */
function shortTimestamp(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const time = d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
  if (d.toDateString() === new Date().toDateString()) return time;
  return `${d.toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  })} ${time}`;
}

/** The investigation title without its " on <queue>" suffix (the full title is the hover text). */
function shortTitle(title: string): string {
  return title.replace(/ on [\w.-]+$/, "");
}

/**
 * One short line under the headline. Redrive/deletion messages carry their result in brackets
 * ("… completed (12 of 12 transaction(s) redriven); check the CBEs …") — show just that result;
 * the next step is on the case page. Anything else (comments, transfers) is shown as written.
 */
function detailLine(item: InvestigationInboxItem): string {
  // Plural "(s)" goes first so it is never mistaken for the bracketed result
  // ("3 message(s) returned …" used to show just "s").
  const text = (item.text ?? "")
    .replace(/\(s\)/g, "s")
    .replace(/\s+att-[0-9a-z]+/gi, "")
    .replace(/\s+after (?:attempt|redrive)$/i, "");
  if (/REDRIVE|DELETION/.test(item.type ?? "")) {
    const result = text.match(/\(([^()]*(?:\([^()]*\)[^()]*)*)\)/);
    if (result) return result[1];
  }
  return text;
}

type Tone = "success" | "error" | "warning" | "info";

function toneOf(type?: string | null): Tone {
  const t = type ?? "";
  if (/FAILED|INCOMPLETE/.test(t)) return "error";
  if (/RETURN/.test(t)) return "warning";
  if (/COMPLETED|VERIFIED|EXECUTED/.test(t)) return "success";
  return "info";
}

const TONE_ICON: Record<Tone, IconProps["name"]> = {
  success: "status-positive",
  error: "status-negative",
  warning: "status-warning",
  info: "status-info",
};
const TONE_VARIANT: Record<Tone, IconProps["variant"]> = {
  success: "success",
  error: "error",
  warning: "warning",
  info: "subtle",
};

const oneLine: React.CSSProperties = {
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const twoLines: React.CSSProperties = {
  overflow: "hidden",
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
};

interface InboxCardProps {
  item: InvestigationInboxItem;
  /** First card in the list: no divider above it. */
  first?: boolean;
  onOpen: (item: InvestigationInboxItem) => void;
  onMarkRead: (item: InvestigationInboxItem) => void;
  marking: boolean;
}

/** A notification as a small clickable card: status icon, headline, case, one-line result. */
const InboxCard: React.FC<InboxCardProps> = ({
  item,
  first = false,
  onOpen,
  onMarkRead,
  marking,
}) => {
  const [hover, setHover] = React.useState(false);
  const unread = !item.readAt;
  const title = item.title || item.investigationId;
  const tone = toneOf(item.type);
  const label = notificationLabel(item.type);
  const detail = detailLine(item);
  const when = shortTimestamp(item.createdAt);
  return (
    <li
      data-testid={`inbox-item-${item.id}`}
      data-unread={unread ? "true" : "false"}
      // Dividers sit between cards only: the panel header already draws the line above the first.
      style={{
        borderTop: first
          ? "none"
          : `1px solid ${tokens.colorBorderDividerDefault}`,
      }}
    >
      <div
        role="link"
        tabIndex={0}
        aria-label={`${label}: ${title}`}
        onClick={() => onOpen(item)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onOpen(item);
          }
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          display: "flex",
          gap: 10,
          padding: "10px 8px",
          cursor: "pointer",
          // Cloudscape CSS variables are hashed, so use the token values, not raw var() names.
          background: hover
            ? tokens.colorBackgroundDropdownItemHover
            : "transparent",
        }}
      >
        <span style={{ paddingTop: 1 }}>
          <Icon name={TONE_ICON[tone]} variant={TONE_VARIANT[tone]} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ ...oneLine, fontWeight: unread ? 700 : 400 }}>
            {label}
          </div>
          <div style={oneLine} title={title}>
            <Box variant="span" fontSize="body-s">
              {shortTitle(title)}
            </Box>
          </div>
          <div
            style={twoLines}
            title={`${detail} · ${formatTimestamp(item.createdAt)}`}
          >
            <Box variant="span" color="text-body-secondary" fontSize="body-s">
              {[detail, when].filter(Boolean).join(" · ")}
            </Box>
          </div>
        </div>
        {unread && (
          // Unread dot; clicking it marks the entry read without opening the case.
          <button
            type="button"
            title="Mark as read"
            aria-label={`Mark "${label}" on ${title} as read`}
            disabled={marking}
            onClick={(e) => {
              e.stopPropagation();
              onMarkRead(item);
            }}
            onKeyDown={(e) => e.stopPropagation()}
            style={{
              alignSelf: "center",
              width: 20,
              height: 20,
              padding: 0,
              border: "none",
              background: "transparent",
              cursor: marking ? "default" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span
              aria-hidden
              style={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: tokens.colorTextAccent,
                opacity: marking ? 0.4 : 1,
              }}
            />
          </button>
        )}
      </div>
    </li>
  );
};

interface InboxListProps {
  items: InvestigationInboxItem[];
  loading: boolean;
  onOpen: (item: InvestigationInboxItem) => void;
  onMarkRead: (item: InvestigationInboxItem) => void;
  marking: boolean;
}

/** The body of the notifications panel: one card per notification, newest first, across every stage. */
export const InvestigationInboxList: React.FC<InboxListProps> = ({
  items,
  loading,
  onOpen,
  onMarkRead,
  marking,
}) => {
  // Read entries are hidden until asked for; the toggle says how many there are.
  const [showRead, setShowRead] = React.useState(false);
  const shown = items.slice(0, PANE_LIMIT);
  const readCount = shown.filter((i) => !!i.readAt).length;
  const visible = showRead ? shown : shown.filter((i) => !i.readAt);
  const toggle = readCount > 0 && (
    <Button variant="inline-link" onClick={() => setShowRead((v) => !v)}>
      {showRead ? "Hide read" : `Show ${readCount} read`}
    </Button>
  );
  const footer = toggle && (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        paddingTop: 8,
      }}
    >
      <span>{toggle}</span>
    </div>
  );
  if (!visible.length) {
    return (
      <SpaceBetween size="xxs">
        <Box color="text-body-secondary" padding={{ vertical: "xs" }}>
          {loading ? "Checking your notifications…" : "You're all caught up."}
        </Box>
        {footer}
      </SpaceBetween>
    );
  }
  return (
    <div>
      <ul
        style={{
          margin: 0,
          padding: 0,
          listStyle: "none",
        }}
      >
        {visible.map((item, index) => (
          <InboxCard
            key={item.id}
            first={index === 0}
            item={item}
            onOpen={onOpen}
            onMarkRead={onMarkRead}
            marking={marking}
          />
        ))}
      </ul>
      {footer}
    </div>
  );
};

/** aria-label prefix of the nav-bar trigger; the panel ignores outside clicks on it (it toggles). */
export const NOTIFICATIONS_TRIGGER_LABEL = "Notifications";

/** The trigger's aria-label, e.g. "Notifications: 2 unread". */
export function notificationsTriggerLabel(unread: number): string {
  return unread
    ? `${NOTIFICATIONS_TRIGGER_LABEL}: ${unread} unread`
    : `${NOTIFICATIONS_TRIGGER_LABEL}: nothing new`;
}

/** Unread count for the nav-bar badge (0 when signed out or the inbox is unavailable). */
export function useUnreadNotifications(currentUser: string | null): number {
  const inbox = useInvestigationInbox(!!currentUser);
  return inbox?.data?.unreadCount ?? 0;
}

/**
 * Viewing a case clears its unread notifications, so the bell does not keep pointing at the page
 * you are already on. Posts once per case visit and lets the server decide what to clear: the
 * inbox page the client holds may not include every unread entry (the badge count is exact).
 */
export function useMarkCaseNotificationsRead(
  currentUser: string | null,
  investigationId: string | null | undefined
): void {
  const mutate = useMarkInboxRead()?.mutate;
  React.useEffect(() => {
    if (currentUser && investigationId && mutate) mutate({ investigationId });
    // Once per signed-in visit of a case, not on every re-render of the mutation object.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, investigationId]);
}

interface NotificationsPanelProps {
  currentUser: string | null;
  open: boolean;
  onClose: () => void;
  /** Open an investigation (the item carries its own stage: the inbox spans all of them). */
  onOpen: (investigationId: string, stage: Stage) => void;
}

/**
 * Notifications side panel, opened from the bell in the top navigation (Cloudscape's notification
 * utility: an icon button with a badge). It is fed by the signed-in user's inbox
 * (`GET /investigations/inbox`): every owner notification across stages, newest first. The same
 * events also go out by email and Slack through the Amazon Notifications service; this panel is the
 * in-app copy. It hangs off the sticky nav bar on the right, so it is available on every page.
 * Opening an entry marks it read and closes the panel; Escape, the close button or a click
 * elsewhere close it too.
 */
export const NotificationsPanel: React.FC<NotificationsPanelProps> = ({
  currentUser,
  open,
  onClose,
  onOpen,
}) => {
  const inbox = useInvestigationInbox(!!currentUser);
  const markRead = useMarkInboxRead() ?? {
    mutateAsync: () => Promise.resolve({ updatedCount: 0, unreadCount: 0 }),
    isLoading: false,
  };
  const [actionError, setActionError] = React.useState<string | null>(null);
  const panelRef = React.useRef<HTMLDivElement>(null);
  // The panel hangs off the sticky nav bar; size it to end at the bottom of the viewport.
  const [navBottom, setNavBottom] = React.useState(0);
  React.useLayoutEffect(() => {
    if (!open) return;
    const nav = panelRef.current?.parentElement;
    if (nav) setNavBottom(Math.max(0, nav.getBoundingClientRect().bottom));
  }, [open]);

  // Keyboard and screen-reader users land in the panel when it opens, and go back to where they
  // were (normally the bell) when it closes. It is a non-modal panel (a click elsewhere closes it),
  // so there is no focus trap.
  const visible = open && !!currentUser;
  React.useEffect(() => {
    if (!visible) return undefined;
    const previous =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    panelRef.current?.focus();
    return () => {
      if (previous?.isConnected) previous.focus();
    };
  }, [visible]);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onPointer = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (!target || panelRef.current?.contains(target)) return;
      // The trigger toggles the panel itself; closing here too would reopen it at once.
      if (target.closest?.(`[aria-label^="${NOTIFICATIONS_TRIGGER_LABEL}"]`))
        return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [open, onClose]);

  if (!open || !currentUser) return null;

  // Fail closed: an unavailable inbox (or a test that mocks the hook away) shows an empty panel.
  const items = inbox?.data?.items ?? [];
  const unread = inbox?.data?.unreadCount ?? 0;

  const report = (what: string) => (err: InvestigationApiError) =>
    setActionError(`Could not ${what}: ${err.message}`);
  const markItem = (item: InvestigationInboxItem) => {
    setActionError(null);
    return markRead
      .mutateAsync({ notificationIds: [item.id] })
      .catch(report("mark the notification as read"));
  };
  const markAll = () => {
    setActionError(null);
    return markRead
      .mutateAsync({ all: true })
      .catch(report("mark all as read"));
  };
  const openItem = (item: InvestigationInboxItem) => {
    if (!item.readAt) {
      markItem(item).catch(console.error);
    }
    onClose();
    onOpen(item.investigationId, item.stage ?? Stage.Gamma);
  };

  return (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal="false"
      aria-label="Notifications"
      tabIndex={-1}
      data-testid="notifications-panel"
      style={{
        position: "absolute",
        top: "100%",
        right: 0,
        width: "min(420px, 100vw)",
        height: `calc(100vh - ${navBottom}px)`,
        overflowY: "auto",
        background: tokens.colorBackgroundContainerContent,
        borderLeft: `1px solid ${tokens.colorBorderDividerDefault}`,
        boxShadow: "-4px 0 12px rgba(0, 0, 0, 0.15)",
      }}
    >
      <Drawer
        header={
          <Header
            variant="h2"
            actions={
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                {unread > 0 && (
                  <Button
                    variant="inline-link"
                    onClick={() => {
                      markAll().catch(console.error);
                    }}
                    loading={markRead.isLoading}
                  >
                    Mark all read
                  </Button>
                )}
                <Button
                  variant="icon"
                  iconName="close"
                  ariaLabel="Close notifications"
                  onClick={onClose}
                />
              </SpaceBetween>
            }
          >
            {unread
              ? `${unread} unread notification${unread === 1 ? "" : "s"}`
              : "Notifications"}
          </Header>
        }
      >
        <SpaceBetween size="s">
          <InvestigationInboxList
            items={items}
            loading={!!inbox?.isLoading}
            onOpen={openItem}
            onMarkRead={(item) => {
              markItem(item).catch(console.error);
            }}
            marking={markRead.isLoading}
          />
          {(inbox?.error || actionError) && (
            <Box color="text-status-error" fontSize="body-s">
              {actionError ??
                `Could not load your notifications: ${
                  inbox?.error?.message ?? "unknown error"
                }`}
            </Box>
          )}
        </SpaceBetween>
      </Drawer>
    </div>
  );
};

export default NotificationsPanel;
